// SessionGuard.js
"use client"
import { useSession, signOut } from "next-auth/react"
import { useRouter, usePathname } from "next/navigation"
import { useEffect } from "react"
import { apiFetch } from "@/lib/api"

export default function SessionGuard() {
  const { data: session, status } = useSession()
  const router = useRouter()
  const pathname = usePathname()

  // Keep apiFetch's username in sync with the signed-in user (session.user.username,
  // from /api/auth/session) so mutating requests can attach it for audit_logs.
  useEffect(() => {
    apiFetch._username = session?.user?.username ?? null
  }, [session?.user?.username])

  // Redirect to login immediately when session becomes unauthenticated
  useEffect(() => {
    if (status === "unauthenticated" && !pathname.startsWith("/login")) {
      router.push("/login")
    }
  }, [status, router, pathname])


  // Log out user when oauth token expires
  // proxy.js will always catch and redirect to login if token is expired, but requires page navigation - this proactively logs out the user
  useEffect(() => {
    if (pathname.startsWith("/login") || !session?.accessTokenExpiresAt) return;

    // Calculate how long until the actual token expires
    const timeRemaining = session.accessTokenExpiresAt - Date.now();

    // If it's already expired, sign out immediately
    if (timeRemaining <= 0) {
      signOut({ callbackUrl: "/login" });
      return;
    }

    // Set a client-side timer to trigger a sign-out when time runs out
    const timer = setTimeout(() => {
      signOut({ callbackUrl: "/login" });
    }, timeRemaining);

    // Clean up timer if the session updates (e.g. user navigates and session extends)
    return () => clearTimeout(timer);
  }, [pathname, session?.accessTokenExpiresAt])

  return null
}